import { useMutation } from 'react-query';
import { Lists } from '~/api/scheme';
import { useForm } from 'react-hook-form';
import { UpdateParams } from '~/api/scheme/lists';
import { useEffect } from 'react';
import useWarnings from '@hooks/useWarnings';
import useListDialogs from '~/hooks/useListDialogs';
import { ListFormValues } from '../types';

const useEditListDialog = () => {
  const { changeWarningsState } = useWarnings();

  const {
    editListOpened: open,
    closeEditListDialog: onClose,
    onClickBack,
    leavePageOpened: isOpenLeavePage,
    onCancel,
    onClickLeaveAnyway,
    editableList: list,
    onEditListSuccessCallback,
    creatableListType,
  } = useListDialogs();

  const mutation = useMutation((params: UpdateParams) => Lists.update(params), {
    onSuccess({ data: { sync_success } }) {
      if (!sync_success && sync_success !== null) {
        changeWarningsState('syncList', true);
      }
      onEditListSuccessCallback?.();
    },
    onError() {
      changeWarningsState('syncList', true);
    },
  });

  const defaultValues: ListFormValues = {
    color: list?.attributes.color || 'primary',
    name: list?.attributes.name || '',
    description: list?.attributes.description || '',
    synced: Boolean(list?.attributes.synced),
    visibility:
      list?.attributes.visibility ||
      (list?.attributes.public || (list?.attributes.shared_user_ids || []).length > 0
        ? 'shared'
        : 'private'),
    shared_scope:
      list?.attributes.shared_scope ||
      (list?.attributes.public
        ? 'all'
        : (list?.attributes.shared_user_ids || []).length > 0
        ? 'specific'
        : 'all'),
    shared_user_ids: (list?.attributes.shared_user_ids || []).map(String),
  };

  const methods = useForm<ListFormValues>({ defaultValues });
  const { reset } = methods;

  useEffect(() => {
    reset(defaultValues);
  }, [reset, list]);

  const toApiAttributes = (values: ListFormValues): UpdateParams['attributes'] => {
    const isShared = values.visibility === 'shared';
    const isSharedAll = isShared && values.shared_scope === 'all';
    const sharedIds =
      isShared && values.shared_scope === 'specific'
        ? values.shared_user_ids.map((id) => Number(id)).filter((id) => !Number.isNaN(id))
        : [];

    return {
      color: values.color,
      description: values.description,
      name: values.name,
      synced: values.synced,
      public: isSharedAll,
      shared_user_ids: sharedIds,
    };
  };

  const onSubmit = () => {
    mutation.mutate({
      id: String(list?.id),
      type: creatableListType,
      attributes: toApiAttributes(methods.getValues()),
    });
  };

  const onReset = () => {
    reset(defaultValues);
  };

  return {
    open,
    onClose,
    onClickBack,
    isOpenLeavePage,
    onCancel,
    onClickLeaveAnyway,
    onSubmit,
    onReset,
    methods,
    creatableListType,
    dialogTitle: 'edit_list',
    submitButtonTitle: 'save',
  };
};

export default useEditListDialog;
